import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDate, formatDateBR, formatDateRange } from "../utils/dateUtils";
import { DependentsList } from "../components/DependentsPanel";
import {
  User,
  Lock,
  Calendar,
  CalendarOff,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Bell,
  BellOff,
  BellRing,
  Send,
  Music,
  Heart,
  Clock,
  Users,
  LogOut
} from "lucide-react";
import usePushNotifications from "../hooks/usePushNotifications";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Push Notifications
  const {
    isSupported: pushSupported,
    permission: pushPermission,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    error: pushError,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTestNotification
  } = usePushNotifications();
  const [testingSend, setTestingSend] = useState(false);
  
  // Password change
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    senha_atual: "",
    nova_senha: "",
    confirmar_senha: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false
  });
  
  // Unavailability
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [showUnavailDialog, setShowUnavailDialog] = useState(false);
  const [editingUnavail, setEditingUnavail] = useState(null);
  const [unavailForm, setUnavailForm] = useState({
    data_inicio: "",
    data_fim: "",
    motivo: "",
    ativo: true
  });
  
  // Personal data editing
  const [personData, setPersonData] = useState(null);
  const [showEditPersonDialog, setShowEditPersonDialog] = useState(false);
  const [personForm, setPersonForm] = useState({
    nome_completo: "",
    telefone: "",
    email: "",
    data_nascimento: "",
    endereco: "",
    bairro: "",
    cidade: "",
    estado: ""
  });

  // Ministries
  const [ministries, setMinistries] = useState([]);
  const [myMinistries, setMyMinistries] = useState([]);
  const [showMinistryDialog, setShowMinistryDialog] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [requestingMinistry, setRequestingMinistry] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [unavailRes, personRes, ministriesRes] = await Promise.all([
        axios.get(`${API}/my-unavailability`),
        axios.get(`${API}/my-person`).catch(() => ({ data: null })),
        axios.get(`${API}/ministries`).catch(() => ({ data: [] }))
      ]);
      setUnavailabilities(unavailRes.data || []);
      setMinistries(ministriesRes.data || []);
      
      if (personRes.data) {
        setPersonData(personRes.data);
        // Extrair ministérios em que a pessoa está
        const meusMinisterios = personRes.data.ministerios_interesse || [];
        setMyMinistries(meusMinisterios);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditPerson = () => {
    if (personData) {
      setPersonForm({
        nome_completo: personData.nome_completo || "",
        telefone: personData.telefone || "",
        email: personData.email || "",
        data_nascimento: personData.data_nascimento || "",
        endereco: personData.endereco || "",
        bairro: personData.bairro || "",
        cidade: personData.cidade || "",
        estado: personData.estado || ""
      });
    }
    setShowEditPersonDialog(true);
  };

  const handleSavePersonData = async () => {
    if (!personForm.nome_completo.trim()) {
      toast.error("Nome completo é obrigatório");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/my-person`, personForm);
      toast.success("Dados atualizados com sucesso!");
      setShowEditPersonDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar dados");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.senha_atual || !passwordForm.nova_senha) {
      toast.error("Preencha todos os campos");
      return;
    }

    if (passwordForm.nova_senha !== passwordForm.confirmar_senha) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (passwordForm.nova_senha.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        senha_atual: passwordForm.senha_atual,
        nova_senha: passwordForm.nova_senha
      });
      
      toast.success("Senha alterada com sucesso!");
      setShowPasswordDialog(false);
      setPasswordForm({ senha_atual: "", nova_senha: "", confirmar_senha: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUnavailDialog = (item = null) => {
    if (item) {
      setEditingUnavail(item);
      setUnavailForm({
        data_inicio: item.data_inicio,
        data_fim: item.data_fim,
        motivo: item.motivo || "",
        ativo: item.ativo !== false
      });
    } else {
      setEditingUnavail(null);
      setUnavailForm({
        data_inicio: "",
        data_fim: "",
        motivo: "",
        ativo: true
      });
    }
    setShowUnavailDialog(true);
  };

  const handleSaveUnavailability = async () => {
    if (!unavailForm.data_inicio || !unavailForm.data_fim) {
      toast.error("Preencha as datas de início e fim");
      return;
    }

    if (unavailForm.data_inicio > unavailForm.data_fim) {
      toast.error("A data de início deve ser anterior à data de fim");
      return;
    }

    setSaving(true);
    try {
      if (editingUnavail) {
        await axios.put(`${API}/my-unavailability/${editingUnavail.id}`, unavailForm);
        toast.success("Período atualizado!");
      } else {
        await axios.post(`${API}/my-unavailability`, unavailForm);
        toast.success("Período de indisponibilidade cadastrado!");
      }
      
      setShowUnavailDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar período");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnavailability = async (itemId) => {
    if (!window.confirm("Deseja remover este período de indisponibilidade?")) return;

    try {
      await axios.delete(`${API}/my-unavailability/${itemId}`);
      toast.success("Período removido!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover período");
    }
  };

  const handleToggleUnavailability = async (item) => {
    try {
      await axios.put(`${API}/my-unavailability/${item.id}`, {
        ...item,
        ativo: !item.ativo
      });
      toast.success(item.ativo ? "Período desativado" : "Período ativado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar período");
    }
  };

  // Ministry functions
  const handleOpenMinistryDialog = (ministry) => {
    setSelectedMinistry(ministry);
    setShowMinistryDialog(true);
  };

  const handleRequestMinistry = async () => {
    if (!selectedMinistry || !personData) return;
    
    setRequestingMinistry(true);
    try {
      await axios.post(`${API}/my-person/request-ministry`, {
        ministerio_id: selectedMinistry.id
      });
      
      const statusMsg = selectedMinistry.precisa_audicao 
        ? "Você foi inscrito para audição!"
        : "Sua solicitação foi enviada e está pendente de aprovação.";
      
      toast.success(statusMsg);
      setShowMinistryDialog(false);
      setSelectedMinistry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao solicitar participação");
    } finally {
      setRequestingMinistry(false);
    }
  };

  const getMinistryStatus = (ministryId) => {
    const interesse = myMinistries.find(m => m.ministerio_id === ministryId);
    return interesse?.status || null;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "aprovado":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">Ativo</Badge>;
      case "em_audicao":
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">Em Audição</Badge>;
      case "pendente_ativacao":
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">Aguardando Aprovação</Badge>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    return formatDateBR(dateStr);
  };

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      const success = await unsubscribePush();
      if (success) {
        toast.success("Notificações desativadas");
      } else {
        toast.error("Erro ao desativar notificações");
      }
    } else {
      const success = await subscribePush();
      if (success) {
        toast.success("Notificações ativadas! Você receberá lembretes das suas escalas.");
      } else {
        toast.error(pushError || "Erro ao ativar notificações");
      }
    }
  };

  const handleTestNotification = async () => {
    setTestingSend(true);
    try {
      const success = await sendTestNotification();
      if (success) {
        toast.success("Notificação de teste enviada!");
      } else {
        toast.error("Erro ao enviar notificação de teste");
      }
    } catch (error) {
      toast.error("Erro ao enviar notificação");
    } finally {
      setTestingSend(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="h-6 w-6 text-primary" />
            Meu Perfil
          </h1>
          <p className="text-muted-foreground">
            Gerencie suas informações e disponibilidade
          </p>
        </div>
      </div>

      {personData?.id && (
        <Card data-testid="my-dependents-card">
          <CardContent className="pt-5">
            <DependentsList responsavelId={personData.id} responsavelTelefone={personData.telefone} />
          </CardContent>
        </Card>
      )}

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informações da Conta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-sm">Nome</Label>
              <p className="font-medium">{user?.nome}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">E-mail</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Alterar Senha</p>
              <p className="text-sm text-muted-foreground">
                Atualize sua senha de acesso
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowPasswordDialog(true)}
              data-testid="change-password-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              Alterar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Personal Data Card */}
      {personData && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Meus Dados Pessoais</CardTitle>
              <CardDescription>Informações do seu cadastro</CardDescription>
            </div>
            <Button variant="outline" onClick={handleOpenEditPerson} data-testid="edit-person-btn">
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Nome Completo</Label>
                <p className="font-medium">{personData.nome_completo}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Telefone</Label>
                <p className="font-medium">{personData.telefone || "-"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">E-mail</Label>
                <p className="font-medium">{personData.email || "-"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Data de Nascimento</Label>
                <p className="font-medium">{personData.data_nascimento ? formatDateBR(personData.data_nascimento) : "-"}</p>
              </div>
              {personData.endereco && (
                <div className="col-span-2">
                  <Label className="text-muted-foreground text-sm">Endereço</Label>
                  <p className="font-medium">
                    {personData.endereco}
                    {personData.bairro && `, ${personData.bairro}`}
                    {personData.cidade && ` - ${personData.cidade}`}
                    {personData.estado && `/${personData.estado}`}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Push Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notificações Push
          </CardTitle>
          <CardDescription>
            Receba lembretes das suas escalas diretamente no celular
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pushSupported ? (
            <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900">
              <div className="flex items-start gap-3">
                <BellOff className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900 dark:text-yellow-100">
                    Não suportado
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                    Seu navegador não suporta notificações push. Tente usar Chrome, Firefox ou Edge.
                  </p>
                </div>
              </div>
            </div>
          ) : pushPermission === 'denied' ? (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
              <div className="flex items-start gap-3">
                <BellOff className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-900 dark:text-red-100">
                    Notificações bloqueadas
                  </p>
                  <p className="text-red-700 dark:text-red-300 mt-1">
                    Você bloqueou as notificações deste site. Para ativar, acesse as configurações do navegador e permita notificações para este site.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  {pushSubscribed ? (
                    <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                      <BellRing className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                  ) : (
                    <div className="p-2 rounded-full bg-muted">
                      <BellOff className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">
                      {pushSubscribed ? "Notificações ativadas" : "Notificações desativadas"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {pushSubscribed 
                        ? "Você receberá lembretes de escalas"
                        : "Ative para receber lembretes"
                      }
                    </p>
                  </div>
                </div>
                <Switch
                  checked={pushSubscribed}
                  onCheckedChange={handleTogglePush}
                  disabled={pushLoading}
                  data-testid="push-toggle"
                />
              </div>

              {pushSubscribed && (
                <Button
                  variant="outline"
                  onClick={handleTestNotification}
                  disabled={testingSend}
                  className="w-full"
                  data-testid="test-notification-btn"
                >
                  {testingSend ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Enviar Notificação de Teste
                </Button>
              )}

              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      Como funciona?
                    </p>
                    <p className="text-blue-700 dark:text-blue-300 mt-1">
                      Quando você for escalado para um evento, receberá uma notificação no seu dispositivo. 
                      Também receberá lembretes antes dos eventos.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Ministries Card */}
      {personData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Music className="h-5 w-5" />
              Meus Ministérios
            </CardTitle>
            <CardDescription>
              Ministérios em que você serve ou deseja servir
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Ministérios em que já serve */}
            {myMinistries.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Ministérios que você participa:</Label>
                <div className="space-y-2">
                  {myMinistries.map((interesse) => {
                    const ministry = ministries.find(m => m.id === interesse.ministerio_id);
                    if (!ministry) return null;
                    return (
                      <div 
                        key={interesse.ministerio_id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Music className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{ministry.nome}</p>
                            {ministry.descricao && (
                              <p className="text-xs text-muted-foreground">{ministry.descricao}</p>
                            )}
                          </div>
                        </div>
                        {getStatusBadge(interesse.status)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ministérios disponíveis */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {myMinistries.length > 0 ? "Outros ministérios disponíveis:" : "Ministérios disponíveis:"}
              </Label>
              <div className="space-y-2">
                {ministries
                  .filter(m => !myMinistries.some(mm => mm.ministerio_id === m.id))
                  .map((ministry) => (
                    <div 
                      key={ministry.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <Music className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{ministry.nome}</p>
                          {ministry.descricao && (
                            <p className="text-xs text-muted-foreground">{ministry.descricao}</p>
                          )}
                          {ministry.precisa_audicao && (
                            <div className="flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3 text-blue-500" />
                              <span className="text-xs text-blue-600">Requer audição</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMinistryDialog(ministry)}
                        data-testid={`request-ministry-${ministry.id}`}
                      >
                        <Heart className="h-4 w-4 mr-1" />
                        Quero Servir
                      </Button>
                    </div>
                  ))}
                
                {ministries.filter(m => !myMinistries.some(mm => mm.ministerio_id === m.id)).length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Você já está inscrito em todos os ministérios</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unavailability Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarOff className="h-5 w-5" />
              Indisponibilidade para Escalas
            </CardTitle>
            <CardDescription>
              Configure os períodos em que você não pode ser escalado
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenUnavailDialog()} data-testid="add-unavail-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Período
          </Button>
        </CardHeader>
        <CardContent>
          {unavailabilities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum período de indisponibilidade cadastrado</p>
              <p className="text-sm">Você está disponível para ser escalado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unavailabilities.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    !item.ativo ? "opacity-50 bg-muted" : "bg-card"
                  }`}
                  data-testid={`unavail-item-${item.id}`}
                >
                  <div className={`p-2 rounded-lg ${item.ativo ? "bg-red-100" : "bg-muted"}`}>
                    <CalendarOff className={`h-5 w-5 ${item.ativo ? "text-red-600" : "text-muted-foreground"}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {formatDate(item.data_inicio)} até {formatDate(item.data_fim)}
                      </p>
                      <Badge variant={item.ativo ? "destructive" : "secondary"}>
                        {item.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>
                    {item.motivo && (
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {item.motivo}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={item.ativo}
                      onCheckedChange={() => handleToggleUnavailability(item)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenUnavailDialog(item)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteUnavailability(item.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  Como funciona?
                </p>
                <p className="text-blue-700 dark:text-blue-300 mt-1">
                  Durante os períodos de indisponibilidade configurados, você ainda aparecerá 
                  na lista de pessoas disponíveis, mas o sistema não permitirá que você seja 
                  escalado para eventos nessas datas.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logout Button */}
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="pt-6">
          <Button 
            variant="outline" 
            className="w-full justify-center gap-2 text-red-600 border-red-200 hover:bg-red-100 hover:text-red-700"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Sair da Conta
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Você será redirecionado para a tela de login
          </p>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Alterar Senha
            </DialogTitle>
            <DialogDescription>
              Digite sua senha atual e a nova senha desejada
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Senha Atual *</Label>
              <div className="relative">
                <Input
                  type={showPasswords.atual ? "text" : "password"}
                  value={passwordForm.senha_atual}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, senha_atual: e.target.value }))}
                  placeholder="Digite sua senha atual"
                  data-testid="senha-atual"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPasswords(prev => ({ ...prev, atual: !prev.atual }))}
                >
                  {showPasswords.atual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nova Senha *</Label>
              <div className="relative">
                <Input
                  type={showPasswords.nova ? "text" : "password"}
                  value={passwordForm.nova_senha}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, nova_senha: e.target.value }))}
                  placeholder="Digite a nova senha"
                  data-testid="nova-senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPasswords(prev => ({ ...prev, nova: !prev.nova }))}
                >
                  {showPasswords.nova ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Mínimo de 6 caracteres</p>
            </div>

            <div className="space-y-2">
              <Label>Confirmar Nova Senha *</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirmar ? "text" : "password"}
                  value={passwordForm.confirmar_senha}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmar_senha: e.target.value }))}
                  placeholder="Confirme a nova senha"
                  data-testid="confirmar-senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirmar: !prev.confirmar }))}
                >
                  {showPasswords.confirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleChangePassword} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Alterar Senha
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unavailability Dialog */}
      <Dialog open={showUnavailDialog} onOpenChange={setShowUnavailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5" />
              {editingUnavail ? "Editar Período" : "Novo Período de Indisponibilidade"}
            </DialogTitle>
            <DialogDescription>
              Configure o período em que você não estará disponível para escalas
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={unavailForm.data_inicio}
                  onChange={(e) => setUnavailForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                  data-testid="unavail-data-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim *</Label>
                <Input
                  type="date"
                  value={unavailForm.data_fim}
                  onChange={(e) => setUnavailForm(prev => ({ ...prev, data_fim: e.target.value }))}
                  data-testid="unavail-data-fim"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={unavailForm.motivo}
                onChange={(e) => setUnavailForm(prev => ({ ...prev, motivo: e.target.value }))}
                placeholder="Ex: Férias, viagem, compromisso pessoal..."
                rows={3}
                data-testid="unavail-motivo"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Desative para ignorar temporariamente este período
                </p>
              </div>
              <Switch
                checked={unavailForm.ativo}
                onCheckedChange={(checked) => setUnavailForm(prev => ({ ...prev, ativo: checked }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowUnavailDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveUnavailability} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingUnavail ? "Salvar Alterações" : "Cadastrar Período"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Personal Data Dialog */}
      <Dialog open={showEditPersonDialog} onOpenChange={setShowEditPersonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Meus Dados</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={personForm.nome_completo}
                onChange={(e) => setPersonForm({ ...personForm, nome_completo: e.target.value })}
                placeholder="Seu nome completo"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={personForm.telefone}
                  onChange={(e) => setPersonForm({ ...personForm, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="space-y-2">
                <Label>Data de Nascimento</Label>
                <Input
                  type="date"
                  value={personForm.data_nascimento}
                  onChange={(e) => setPersonForm({ ...personForm, data_nascimento: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={personForm.email}
                onChange={(e) => setPersonForm({ ...personForm, email: e.target.value })}
                placeholder="seu@email.com"
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Endereço</Label>
              <Input
                value={personForm.endereco}
                onChange={(e) => setPersonForm({ ...personForm, endereco: e.target.value })}
                placeholder="Rua, número"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Bairro</Label>
                <Input
                  value={personForm.bairro}
                  onChange={(e) => setPersonForm({ ...personForm, bairro: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input
                  value={personForm.cidade}
                  onChange={(e) => setPersonForm({ ...personForm, cidade: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Input
                  value={personForm.estado}
                  onChange={(e) => setPersonForm({ ...personForm, estado: e.target.value })}
                  maxLength={2}
                  placeholder="UF"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEditPersonDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePersonData} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ministry Request Dialog */}
      <Dialog open={showMinistryDialog} onOpenChange={setShowMinistryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Quero Servir
            </DialogTitle>
            <DialogDescription>
              Confirme sua solicitação para participar do ministério
            </DialogDescription>
          </DialogHeader>

          {selectedMinistry && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3 mb-2">
                  <Music className="h-5 w-5 text-primary" />
                  <span className="font-medium text-lg">{selectedMinistry.nome}</span>
                </div>
                {selectedMinistry.descricao && (
                  <p className="text-sm text-muted-foreground">{selectedMinistry.descricao}</p>
                )}
              </div>

              {selectedMinistry.precisa_audicao ? (
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        Este ministério requer audição
                      </p>
                      <p className="text-blue-700 dark:text-blue-300 mt-1">
                        Você será inscrito no processo de audição. Um líder entrará em contato para agendar sua avaliação.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-900 dark:text-yellow-100">
                        Pendente de aprovação
                      </p>
                      <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                        Sua solicitação será analisada por um líder do ministério. Você será notificado quando for aprovado.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowMinistryDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRequestMinistry} disabled={requestingMinistry}>
              {requestingMinistry ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirmar Solicitação
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
